// Telemetry hook (Unit 41, spec §13.8).
//
// A closed-taxonomy event API. Events batch for ~1 second before being
// POSTed to `/api/log/batch`; `beforeunload` triggers a final flush so the
// last few clicks of a session aren't lost. A failing endpoint is silently
// swallowed — telemetry must never break the UI.
//
// Payload rules per spec: never include transcript text, annotation note
// content, original/corrected words, paper names, student names. IDs and
// statuses only.

import { useEffect, useRef } from "react";

import { apiFetch } from "../api/client";

export type TelemetryEventName =
  | "app.load"
  | "project.create"
  | "project.edit"
  | "paper.upload"
  | "paper.review.open"
  | "paper.review.close"
  | "annotation.accept"
  | "annotation.dismiss"
  | "annotation.edit"
  | "annotation.manual.create"
  | "ocr_flag.confirm"
  | "ocr_flag.correct"
  | "transcript.toggle"
  | "paper.model_solution.open"
  | "paper.export"
  | "project.export"
  | "settings.open"
  | "error"
  | "shortcut.help.open"
  | "welcome.dismiss"
  | "welcome.tour";

interface TelemetryEvent {
  level?: "info" | "warning" | "error";
  event: TelemetryEventName;
  payload?: Record<string, unknown>;
}

// Module-singleton so every component shares one queue and one timer.
let queue: TelemetryEvent[] = [];
let timer: number | null = null;

function scheduleFlush() {
  if (timer != null) return;
  timer = window.setTimeout(() => {
    timer = null;
    void flush();
  }, 1000);
}

async function flush() {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    await apiFetch("/api/log/batch", {
      method: "POST",
      body: JSON.stringify({
        events: batch.map((e) => ({
          level: e.level ?? "info",
          event: e.event,
          payload: e.payload ?? {},
        })),
      }),
    });
  } catch {
    // Drop on failure. Telemetry is best-effort.
  }
}

export function trackEvent(event: TelemetryEventName, payload: Record<string, unknown> = {}): void {
  queue.push({ event, payload });
  scheduleFlush();
}

export function useTelemetry(): { track: typeof trackEvent } {
  const beforeUnloadBound = useRef(false);
  useEffect(() => {
    if (beforeUnloadBound.current) return;
    beforeUnloadBound.current = true;
    function onBeforeUnload() {
      // sendBeacon avoids being cancelled by page unload.
      if (queue.length > 0 && "sendBeacon" in navigator) {
        try {
          const blob = new Blob(
            [
              JSON.stringify({
                events: queue.map((e) => ({
                  level: e.level ?? "info",
                  event: e.event,
                  payload: e.payload ?? {},
                })),
              }),
            ],
            { type: "application/json" },
          );
          navigator.sendBeacon("/api/log/batch", blob);
          queue = [];
        } catch {
          // Ignore — best-effort.
        }
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  return { track: trackEvent };
}
